<script setup lang="ts">
import type { CmsElementBuyBox } from "@shopware/composables";
import { ApiClientError } from "@shopware/api-client";
import { decodeHTML } from "entities";
import type { Schemas } from "#shopware";
import type { VNode, VNodeArrayChildren } from "vue";
import type { NodeObject } from "@shopware/cms-base-layer/helpers/html-to-vue/getOptionsFromNode";
import { getOptionsFromNode } from "@shopware/cms-base-layer/helpers/html-to-vue/getOptionsFromNode";
import { renderHtml } from "@shopware/cms-base-layer/helpers/html-to-vue/renderToHtml";
type RawChildren = string | number | boolean | VNode | VNodeArrayChildren;

import {
  useCmsElementConfig,
  useProductPrice,
  useSessionContext,
  useProduct,
  usePrice,
  useShopwareContext,
} from "#imports";
import SwVariantConfigurator from "@shopware/cms-base-layer/components/SwVariantConfigurator.vue";
import SwSharedPrice from "@shopware/cms-base-layer/components/SwSharedPrice.vue";
import { useWindowSize } from "@vueuse/core";
import { productDetailTracking } from "~/components/utilities/GtmTrackings";
const { getPaymentMethods } = useCheckout();
const payments = await getPaymentMethods();
const { apiClient } = useShopwareContext();

const props = defineProps<{
  content: CmsElementBuyBox;
}>();

const { getConfigValue } = useCmsElementConfig(props.content);
const alignment = computed(() => getConfigValue("alignment"));

const { taxState, currency } = useSessionContext();

const { product, changeVariant } = useProduct(
  props.content.data.product,
  props.content.data.configuratorSettings || []
);

const { unitPrice, price, tierPrices, isListPrice } = useProductPrice(product);
const regulationPrice = computed(() => price.value?.regulationPrice?.price);
const { getFormattedPrice, currencyCode } = usePrice();

const referencePrice = computed(
  () => product.value?.calculatedPrice?.referencePrice
);
const productNumber = computed(() => product.value?.productNumber);
const availableStock = computed(() => product.value?.availableStock ?? 0);
const minPurchase = computed(() => product.value?.minPurchase ?? 0);
const maxPurchase = computed(() => product.value.calculatedMaxPurchase || 1);
const deliveryTime = computed(() => product.value?.deliveryTime);
const restockTime = computed(() => product.value?.restockTime);
const purchaseUnit = computed(() => product.value?.purchaseUnit);
const priceHidden = computed(
  () =>
    product.value?.translated?.customFields?.custom_product_fields_hide_price
);

const taxRate = computed(() => price.value?.taxRules?.[0]?.taxRate || 0);

const getNetPrice = (grossPrice: number) => {
  if (taxState.value === "net") return grossPrice;
  return grossPrice ? grossPrice / (1 + taxRate.value / 100) : 0;
};

const getGrossPrice = (netPrice: number) => {
  if (taxState.value === "gross") return netPrice;
  return netPrice ? netPrice * (1 + taxRate.value / 100) : 0;
};

const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);
const { pushSuccess, pushError } = useNotifications();
const { t } = useI18n();

const toggleWishlistProduct = async () => {
  if (!isInWishlist.value) {
    try {
      await addToWishlist();
      return pushSuccess(
        t(`product.messages.addedToWishlist`, {
          p: product.value?.name,
        })
      );
    } catch (error) {
      if (error instanceof ApiClientError) {
        const reason = error.details.errors?.[0]?.detail
          ? `Reason: ${error.details.errors?.[0]?.detail}`
          : "";
        return pushError(
          `${product.value?.name} cannot be added to wishlist.\n${reason}`,
          {
            timeout: 5000,
          }
        );
      }
    }
  }
  removeFromWishlist();
};

const { width } = useWindowSize();

const reviews: Ref<Schemas["ProductReview"][]> = ref([]);
const reviewCount: Ref<number> = ref(0);

const fetchReviews = async () => {
  try {
    const reviewsResponse = await apiClient.invoke(
      "readProductReviews post /product/{productId}/reviews",
      {
        pathParams: { productId: product.value.id },
      }
    );

    reviews.value = reviewsResponse.data.elements || [];
    reviewCount.value = reviewsResponse.data.total || 0; // Fetch total review count
  } catch (error) {
    console.error("Error fetching reviews:", error);
  }
};

fetchReviews();

const productDescription = computed(
  () => product.value?.translated?.description
);

const context = getCurrentInstance();

const CmsProductDescriptionRender = defineComponent({
  setup() {
    const { resolveUrl } = useUrlResolver();

    const config = {
      textTransformer: (text: string) => decodeHTML(text),
      extraComponentsMap: {
        link: {
          conditions(node: NodeObject) {
            return (
              node.type === "tag" &&
              node.name === "a" &&
              !node.attrs?.class?.match(/btn\s?/)
            );
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            const _class = node?.class;
            return createElement(
              "a",
              {
                class: _class,
                ...getOptionsFromNode(node, resolveUrl).attrs,
                href: node.attrs?.href,
              },
              [...children]
            );
          },
        },
        button: {
          conditions(node: NodeObject) {
            return (
              node.type === "tag" &&
              node.name === "a" &&
              node.attrs?.class?.match(/btn\s?/)
            );
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            let _class = "";
            if (node?.class) {
              const btnClass = "";

              _class = node?.class
                ?.replace("btn-secondary", `${btnClass} btn-outline`)
                .replace("btn-primary", `${btnClass} btn`);
            }

            return createElement(
              "a",
              {
                class: _class,
                ...getOptionsFromNode(node, resolveUrl).attrs,
              },
              [...children]
            );
          },
        },
        font: {
          conditions(node: NodeObject) {
            return node.type === "tag" && node.name === "font";
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            // convert from <font color="#ce0000">Headline 1</font> to <span style="color:#ce0000">Headline 1</span>
            let newStyle = null;
            const styleColor = node?.attrs?.color;
            if (styleColor) {
              const currentStyle = node.attrs?.style ?? "";
              newStyle = `color:${styleColor};${currentStyle}`;
              delete node.attrs?.color;
            }

            return createElement(
              "span",
              {
                style: newStyle,
                ...getOptionsFromNode(node, resolveUrl).attrs,
              },
              [...children]
            );
          },
        },
        img: {
          conditions(node: NodeObject) {
            return node.type === "tag" && node.name === "img";
          },
          renderer(
            node: NodeObject,
            children: RawChildren[],
            createElement: typeof h
          ) {
            return createElement(
              "img",
              getOptionsFromNode(node, resolveUrl)?.attrs
            );
          },
        },
      },
    };
    const rawHtml =
      productDescription.value?.length > 0
        ? productDescription.value
        : "<div class='cms-element-text missing-content-element'></div>";

    return () =>
      h("div", {}, renderHtml(rawHtml, config, h, context, resolveUrl));
  },
});

if (import.meta.client) {
  productDetailTracking(product.value);
}

const selectedTierPrice = ref(1);
const quantity = ref(product.value?.minPurchase || 1);
const tierSumPrice = ref(0);
const selectTierPriceQuantity = (tierPrice: any, index: number) => {
  // Set the quantity based on tier price selection
  if (index === 0) {
    quantity.value = product.value?.minPurchase || 1;
  } else if (index < tierPrices.value.length - 1) {
    quantity.value = tierPrices.value[index - 1]?.quantity + 1 || 1;
  } else {
    quantity.value = tierPrice.quantity || 1;
  }

  if (maxPurchase.value < quantity.value) return false;

  selectedTierPrice.value = tierPrice.quantity;
  tierSumPrice.value = tierPrice.unitPrice * quantity.value;
};

onMounted(() => {
  if (tierPrices.value.length > 0) {
    tierSumPrice.value = tierPrices.value[0].unitPrice * quantity.value;
  }
});

const formattedPrice = (price: number) => {
  const formatted = getFormattedPrice(price);
  return `${currencyCode.value} ${formatted.replace(",", ".").replace(currencyCode.value, "")}`;
};

const showAllTierPrices = ref(false);

const displayedTierPrices = computed(() => {
  if (showAllTierPrices.value || tierPrices.value.length <= 2) {
    return tierPrices.value;
  }
  return tierPrices.value.slice(0, 2);
});
</script>
<template>
  <div class="relative">
    <div
      v-if="product?.isNew"
      class="inline-block font-bold bg-skin-theme text-skin-white text-sm px-5 rounded-full mb-5"
    >
      {{ $t("product.badges.new") }}
    </div>
    <button
      class="btn-default btn-transprent-bg bg-transparent text-xl btn-w-icon justify-center absolute right-0 mt-1"
      @click="toggleWishlistProduct"
    >
      <span class="ml-5">
        <i v-if="isInWishlist" class="fa-heart fa-solid text-skin-theme"></i>
        <i v-else class="fa-heart fa-regular"></i>
      </span>
    </button>
    <h1
      class="text-2xl md:text-4xl font-bold mb-5 text-skin-primary max-w-[calc(100%-30px)]"
    >
      {{ product?.name || product?.translated.name }}
    </h1>
    <p class="text-sm text-skin-primary">
      {{ $t("product.productNumber") }}: {{ productNumber }}
    </p>

    <div class="product-buy-box">
      <ProductDetailProductVariantConfigurator @change="changeVariant" />
    </div>

    <div v-if="product?.ratingAverage" class="flex items-center gap-x-1.5 mb-5">
      <div class="w-6 h-6 bg-empty-star bg-[length:100%] bg-no-repeat">
        <span
          :style="{
            width: `${(product.ratingAverage / 5) * 100}%`,
          }"
          class="block w-6 h-6 bg-field-star bg-[length:auto_100%] bg-no-repeat"
        ></span>
      </div>
      <h6>
        <b class="font-bold text-skin-primary"
          >{{ product?.ratingAverage }}/5
        </b>
        <span v-if="reviewCount"
          >({{ reviewCount }} {{ $t("product.reviews") }})</span
        >
      </h6>
    </div>

    <h4
      v-if="availableStock >= minPurchase"
      class="flex gap-x-2.5 mb-5 items-center"
    >
      <span
        ><i class="inline-block w-4 h-4 bg-star-tick"></i>
        {{ $t("product.noAvailable") }}
      </span>
    </h4>

    <template v-if="!priceHidden">
      <div
        v-if="tierPrices.length <= 1"
        class="flex flex-wrap items-center gap-x-5 mb-5"
      >
        <span v-if="isListPrice" class="inline-flex items-center gap-x-5">
          <SharedPrice
            class="font-bold text-skin-theme !text-[18px] line-through mb-0 mt-[-5px] p-2"
            :value="price?.listPrice?.price"
          />
        </span>

        <div class="inline-flex items-center gap-x-5 mb-0">
          <ProductDetailProductPrice
            v-if="unitPrice"
            class="font-bold !text-4xl mb-0"
            :class="{
              'text-maximum-red': isListPrice,
            }"
            :value="unitPrice"
          />
        </div>

        <div v-if="regulationPrice" class="text-xs flex text-secondary-500">
          {{ $t("productDetails.previously") }}
          <ProductDetailProductPrice class="ml-1" :value="regulationPrice" />
        </div>
      </div>
    </template>
    <template v-if="!priceHidden">
      <div v-if="tierPrices.length > 1">
        <h3 class="text-sm text-gray-500 font-medium mb-3">
          {{ $t("product.price.label") }}
        </h3>
        <ul
          class="flex flex-wrap gap-x-2.5 gap-y-2.5 font-bold text-skin-secondary [&>li>input:checked+label]:border-skin-theme"
        >
          <li
            v-for="(tierPrice, index) in displayedTierPrices"
            :key="tierPrice.label"
            data-testid="product-variant"
            class="relative w-full"
          >
            <label
              class="flex flex-row justify-between items-center px-5 bg-skin-icon rounded-full py-2 gap-y-1 border-2 hover:cursor-pointer"
              @click="selectTierPriceQuantity(tierPrice, index)"
              :class="{
                'cursor-default border-skin-theme bg-[#eff6f2]':
                  selectedTierPrice === tierPrice.quantity,
              }"
              data-testid="product-variant-text"
            >
              <div class="flex items-center gap-x-2.5">
                {{ $t("product.from") }}
                <span v-if="index == 0">
                  {{ product?.minPurchase || 1 }}
                </span>
                <span
                  v-else-if="
                    index < displayedTierPrices.length - 1 || !showAllTierPrices
                  "
                >
                  {{ displayedTierPrices[index - 1].quantity + 1 }}
                </span>
                <span v-else>
                  {{ tierPrice.quantity }}
                </span>
              </div>

              <!-- Unit Price -->
              <div>
                <span v-if="purchaseUnit" class="font-medium text-gray-700">
                  {{
                    formattedPrice(
                      getNetPrice(tierPrice.unitPrice / purchaseUnit)
                    )
                  }}/{{ product.unit?.name }}
                </span>
              </div>

              <!-- Pricing & Purchase Unit Info -->
              <div>
                <div>
                  <SharedPrice
                    class="font-medium text-gray-700"
                    :value="tierPrice.unitPrice"
                  />
                </div>
              </div>
            </label>
          </li>
        </ul>

        <!-- Show More/Less Button -->
        <div v-if="tierPrices.length > 2" class="mb-4 text-right">
          <button
            @click="showAllTierPrices = !showAllTierPrices"
            class="text-skin-theme hover:text-skin-theme-hover font-medium flex items-center justify-end gap-2 w-full"
          >
            {{
              showAllTierPrices
                ? t("product.price.showLess")
                : t("product.price.showMore")
            }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 transition-transform duration-200"
              :class="{ 'rotate-180': showAllTierPrices }"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div v-if="tierSumPrice" class="font-bold !text-4xl mb-0">
          <ProductDetailProductPrice :value="tierSumPrice" />
        </div>
      </div>
    </template>

    <div v-if="product" class="flex flex-wrap flex-col xs:flex-row gap-2.5">
      <ProductDetailProductAddToCart
        :product="product"
        :tier-quantity="quantity"
      />
    </div>

    <div class="product-description mt-8"><CmsProductDescriptionRender /></div>
  </div>
</template>
